// src/pages/gerenciar/TabelaOSPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useEmpresa } from "../../contexts/EmpresaContext";
import { nivelUsuario } from "../../utils/permissoes";
import { NIVEIS } from "../../config/niveis";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import FiltroBar from "../../components/FiltroBar/FiltroBar";

import { db } from "../../config/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";

import { formatData, formatDataHora } from "../../utils/dateFormat";
import { useSortableTable, SortableHeader } from "../../hooks/useSortableTable";

const OS_CACHE_TTL_MS = 60000;
const STATUS_CACHE_TTL_MS = 300000;
const ordensCache = new Map();
const statusListCache = new Map();

function renderCodigoOs(os) {
  if (os.codigo) return os.codigo;
  if (os.codigoSequencial != null) {
    return `OS${String(os.codigoSequencial).padStart(4, "0")}`;
  }
  if (os.id) return os.id.slice(0, 8);
  return "-";
}


function formatParaInput(data) {
  if (!data) return "";
  const d = data?.toDate ? data.toDate() : (data instanceof Date ? data : new Date(data));
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ChevronIcon({ expanded }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transition: "transform 0.2s ease",
        transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
      }}
    >
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );
}

function TabelaOSPage() {
  const { tema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const { empresaAtual, modoGlobal } = useEmpresa();
  const nivel = nivelUsuario(usuarioLogado);
  const grupoId = usuarioLogado?.grupoId || null;
  const empresaIdAtual = empresaAtual?.id || null;
  const empresaNomeAtual = String(empresaAtual?.nome || "").trim();
  const loadRequestRef = useRef(0);
  const statusRequestRef = useRef(0);
  const podeEditarHorarios = nivel >= NIVEIS.GESTOR;
  const navigate = useNavigate();

  const [ordens, setOrdens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [deletando, setDeletando] = useState(null);

  const [statusList, setStatusList] = useState([]);
  const [modalTrocaStatus, setModalTrocaStatus] = useState(null);
  const [novoStatusId, setNovoStatusId] = useState("");

// Normaliza ids antigos para manter compatibilidade e evitar duplicidade
const normalizarStatusId = (id) => {
  if (!id) return "";
  if (id === "fab_aguard_material") return "fab_aguard_mat";
  return id;
};

  const [salvandoStatus, setSalvandoStatus] = useState(false);

  const [campoFiltro, setCampoFiltro] = useState("codigo");
  const [textoFiltro, setTextoFiltro] = useState("");

  const [fEmpresa, setFEmpresa] = useState("todas");
  const [fSetor, setFSetor] = useState("todas");
  const [fMaquina, setFMaquina] = useState("todas");
  const [fStatus, setFStatus] = useState("todas");
  const [fParada, setFParada] = useState("todas");

  // Força escopo de empresa para níveis baixos (Cliente/Kiosk/Técnico), evitando "todas"
  useEffect(() => {
    const nivel = nivelUsuario(usuarioLogado);
    const restringirEmpresa = !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;
    if (!restringirEmpresa) return;

    const eid = empresaAtual?.id || null;
    const en = String(empresaAtual?.nome || "").trim();
    const alvo = eid || en || "todas";
    if (alvo !== "todas" && fEmpresa !== alvo) setFEmpresa(alvo);
  }, [usuarioLogado, modoGlobal, empresaAtual, fEmpresa]);


  const camposFiltro = [
    { value: "codigo", label: "Código" },
    { value: "titulo", label: "Título" },
    { value: "empresaNome", label: "Empresa" },
    { value: "setorNome", label: "Setor" },
    { value: "maquinaNome", label: "Máquina" },
  ];

  useEffect(() => {
    async function carregarOs() {
      if (!grupoId) return;

      const requestId = loadRequestRef.current + 1;
      loadRequestRef.current = requestId;

      const isStale = () => loadRequestRef.current !== requestId;

      setCarregando(true);
      setErro("");

      try {
        const osRef = collection(db, "ordensServico");

        const restringirEmpresa = !modoGlobal || nivel < NIVEIS.TECNICO_SENIOR;
        const empresaEscopo = restringirEmpresa
          ? (empresaIdAtual || empresaNomeAtual || "__sem_empresa__")
          : "__todas__";
        const cacheKey = `${grupoId}|${restringirEmpresa ? "restrito" : "global"}|${empresaEscopo}`;
        const cached = ordensCache.get(cacheKey);

        if (cached && Date.now() - cached.at < OS_CACHE_TTL_MS) {
          if (!isStale()) {
            setOrdens(cached.data);
            setCarregando(false);
          }
          return;
        }

        // Para níveis baixos (Cliente/Kiosk/Técnico), nunca buscar todas as OS do grupo
        // (evita vazamento de dados e reduz tráfego). Para níveis altos, respeita modoGlobal.
        let qOs = query(osRef, where("grupoId", "==", grupoId));

        if (restringirEmpresa) {
          const eid = empresaIdAtual;
          const enome = empresaNomeAtual;

          if (eid) {
            qOs = query(osRef, where("grupoId", "==", grupoId), where("empresaId", "==", eid));
          } else if (enome) {
            // fallback para bases antigas
            qOs = query(osRef, where("grupoId", "==", grupoId), where("empresaNome", "==", enome));
          }
        }
        const snapOs = await getDocs(qOs);
        if (isStale()) return;

        const listaOs = snapOs.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        if (!isStale()) {
          setOrdens(listaOs);
          ordensCache.set(cacheKey, { at: Date.now(), data: listaOs });
        }
      } catch (e) {
        console.error("Erro ao carregar OS:", e);
        if (!isStale()) {
          setErro("Erro ao carregar Tabela de OS. Tente novamente.");
        }
      } finally {
        if (!isStale()) {
          setCarregando(false);
        }
      }
    }

    carregarOs();

    return () => {
      loadRequestRef.current += 1;
    };
  }, [grupoId, modoGlobal, nivel, empresaIdAtual, empresaNomeAtual]);

  useEffect(() => {
    async function carregarStatusList() {
      if (!grupoId) return;

      const requestId = statusRequestRef.current + 1;
      statusRequestRef.current = requestId;
      const isStale = () => statusRequestRef.current !== requestId;

      const cached = statusListCache.get(grupoId);
      if (cached && Date.now() - cached.at < STATUS_CACHE_TTL_MS) {
        if (!isStale()) {
          setStatusList(cached.data);
        }
        return;
      }

      try {
        const statusRef = collection(db, "statusOs");
        const qStatus = query(statusRef, where("grupoId", "==", grupoId));
        const snapStatus = await getDocs(qStatus);
        if (isStale()) return;

        const listaStatusDb = snapStatus.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        const statusFabrica = [
          { id: "fab_aberta", nome: "Aberta", cor: "#2563eb" },
          { id: "fab_aberta_24h", nome: "Aberta 24H", cor: "#7c3aed" },
          { id: "fab_em_atendimento", nome: "Em Atendimento", cor: "#0ea5e9" },
          { id: "fab_solic_mat", nome: "Solicitação de Material", cor: "#d97706" },
          { id: "fab_aguard_mat", nome: "Aguardando Material", cor: "#ca8a04" },
          { id: "fab_aguard_parada", nome: "Aguardando Parada de Máquina", cor: "#64748b" },
          { id: "fab_finalizada", nome: "Finalizada", cor: "#16a34a" },
          { id: "fab_cancelada", nome: "Cancelada", cor: "#dc2626" },
        ];

        const nextStatus = [...statusFabrica, ...listaStatusDb];
        if (!isStale()) {
          setStatusList(nextStatus);
          statusListCache.set(grupoId, { at: Date.now(), data: nextStatus });
        }
      } catch (e) {
        console.error("Erro ao carregar status:", e);
      }
    }

    carregarStatusList();

    return () => {
      statusRequestRef.current += 1;
    };
  }, [grupoId]);

  const handleAbrirTrocaStatus = (os) => {
    setModalTrocaStatus(os);
    setNovoStatusId(normalizarStatusId(os.statusId || ""));
  };

  const handleFecharTrocaStatus = () => {
    setModalTrocaStatus(null);
    setNovoStatusId("");
  };

  const handleSalvarNovoStatus = async () => {
    if (!modalTrocaStatus || !novoStatusId) return;

    const statusSelecionado = statusList.find((s) => normalizarStatusId(s.id) === normalizarStatusId(novoStatusId));
    if (!statusSelecionado) return;

    setSalvandoStatus(true);

    try {
      const osRef = doc(db, "ordensServico", modalTrocaStatus.id);
      await updateDoc(osRef, {
        statusId: statusSelecionado.id,
        statusNome: statusSelecionado.nome,
        statusCor: statusSelecionado.cor || null,
        atualizadoPor: usuarioLogado?.uid || null,
        atualizadoPorNome: usuarioLogado?.nome || null,
        updatedAt: serverTimestamp(),
      });

      setOrdens((prev) =>
        prev.map((os) =>
          os.id === modalTrocaStatus.id
            ? {
                ...os,
                statusId: statusSelecionado.id,
                statusNome: statusSelecionado.nome,
                statusCor: statusSelecionado.cor || null,
              }
            : os
        )
      );

      const nomeStatusNormalizado = (statusSelecionado.nome || "").toLowerCase().trim();
      const isAguardandoMaterial = nomeStatusNormalizado === "aguardando material";

      const osIdParaRedirecionar = modalTrocaStatus.id;

      handleFecharTrocaStatus();

      if (isAguardandoMaterial) {
        navigate(`/estoque/pedidos-pecas/os/${osIdParaRedirecionar}`);
      }
    } catch (e) {
      console.error("Erro ao atualizar status:", e);
      alert("Erro ao atualizar status. Tente novamente.");
    } finally {
      setSalvandoStatus(false);
    }
  };

  const empresasUnicas = useMemo(() => {
    const map = new Map();
    for (const os of ordens) {
      if (os.empresaId) {
        map.set(os.empresaId, os.empresaNome || os.empresaId);
      } else if (os.empresaNome) {
        // legado: quando não havia empresaId
        map.set(os.empresaNome, os.empresaNome);
      }
    }
    return Array.from(map.entries()).map(([id, nome]) => ({ id, nome }));
  }, [ordens]);

  const setoresUnicos = useMemo(() => {
    const map = new Map();
    for (const os of ordens) {
      if (os.setorId) {
        map.set(os.setorId, os.setorNome || os.setorId);
      }
    }
    return Array.from(map.entries()).map(([id, nome]) => ({ id, nome }));
  }, [ordens]);

  const maquinasUnicas = useMemo(() => {
    const map = new Map();
    for (const os of ordens) {
      if (os.maquinaId) {
        map.set(os.maquinaId, os.maquinaNome || os.maquinaId);
      }
    }
    return Array.from(map.entries()).map(([id, nome]) => ({ id, nome }));
  }, [ordens]);

  const statusUnicos = useMemo(() => {
    const map = new Map();
    for (const os of ordens) {
      const chave = os.statusId || os.statusNome || "sem-status";
      const nome = os.statusNome || "Sem status";
      const cor = os.statusCor || null;
      if (!map.has(chave)) {
        map.set(chave, { id: os.statusId || null, nome, cor });
      }
    }
    return Array.from(map.values());
  }, [ordens]);

  const ordensFiltradas = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    return ordens.filter((os) => {
      if (fEmpresa !== "todas") {
        const eid = os.empresaId || null;
        const en = String(os.empresaNome || "").trim();
        if (eid) {
          if (eid !== fEmpresa) return false;
        } else if (en) {
          if (en !== fEmpresa) return false;
        } else {
          return false;
        }
      }
      if (fSetor !== "todas" && os.setorId !== fSetor) return false;
      if (fMaquina !== "todas" && os.maquinaId !== fMaquina) return false;
      if (fStatus !== "todas") {
        const idComparar = os.statusId || null;
        if (idComparar !== fStatus) return false;
      }

      if (fParada === "sim") {
        const parou = String(os.maquinaParou || "").toLowerCase();
        if (!["sim", "s", "true", "1", "yes"].includes(parou)) return false;
      } else if (fParada === "nao") {
        const parou = String(os.maquinaParou || "").toLowerCase();
        if (!["nao", "não", "n", "false", "0", "no"].includes(parou)) return false;
      }

      if (!texto) return true;

      let valorBase = "";
      if (campoFiltro === "codigo") {
        valorBase = renderCodigoOs(os);
      } else {
        valorBase = os[campoFiltro] ?? "";
      }

      return String(valorBase).toLowerCase().includes(texto);
    });
  }, [ordens, textoFiltro, campoFiltro, fEmpresa, fSetor, fMaquina, fStatus, fParada]);

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const { sortedData, requestSort, getSortDirection } = useSortableTable(
    ordensFiltradas,
    { key: "criadoEm", direction: "desc" }
  );

  async function handleExcluir(osId) {
    if (!osId) return;

    const confirmar = window.confirm(
      "Tem certeza que deseja excluir esta OS? Essa ação não pode ser desfeita."
    );
    if (!confirmar) return;

    setDeletando(osId);
    setErro("");

    try {
      // ⚠️ Regra: ao excluir uma OS, também excluímos TODOS os atendimentos vinculados.
      // Observação: usamos paginação simples (chunks) por limite de batch.
      const osAtual = ordens.find((o) => o.id === osId) || null;
      const grupoId = usuarioLogado?.grupoId || osAtual?.grupoId;

      if (grupoId) {
        const atendRef = collection(db, "atendimentos");
        const qAt = query(
          atendRef,
          where("grupoId", "==", grupoId),
          where("osId", "==", osId)
        );
        const snapAt = await getDocs(qAt);

        if (!snapAt.empty) {
          const docs = snapAt.docs;
          // batch máximo 500 ops. Usamos 450 pra folga.
          const CHUNK = 450;
          for (let i = 0; i < docs.length; i += CHUNK) {
            const batch = writeBatch(db);
            const parte = docs.slice(i, i + CHUNK);
            parte.forEach((d) => batch.delete(d.ref));
            await batch.commit();
          }
        }
      }

      await deleteDoc(doc(db, "ordensServico", osId));
      setOrdens((prev) => prev.filter((os) => os.id !== osId));
      if (expandedId === osId) setExpandedId(null);
    } catch (e) {
      console.error("Erro ao excluir OS:", e);
      setErro("Erro ao excluir OS. Tente novamente.");
    } finally {
      setDeletando(null);
    }
  }

  const handleEditar = (os) => {
    navigate(`/ordens-servico/editar/${os.id}`);
  };

  const handleVerDetalhes = (os) => {
    navigate(`/ordens-servico/atendimento/${os.id}`);
  };

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Tabela de Ordens de Serviço"
      subtitulo="Gerenciar / Tabela de OS"
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Tabela de OS</h3>
            <p>
              Visão geral das Ordens de Serviço, com filtros por empresa, setor,
              máquina e status. Clique na seta para ver detalhes completos.
            </p>
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          onAdicionarClick={null}
          placeholder="Filtrar OS..."
          mostrarAdicionar={false}
        />

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 12,
            marginTop: 8,
          }}
        >
          <select
            value={fEmpresa}
            onChange={(e) => setFEmpresa(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #334155",
              
              fontSize: 12,
            }}
          >
            <option value="todas">Todas empresas</option>
            {empresasUnicas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nome}
              </option>
            ))}
          </select>

          <select
            value={fSetor}
            onChange={(e) => setFSetor(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #334155",
              
              fontSize: 12,
            }}
          >
            <option value="todas">Todos setores</option>
            {setoresUnicos.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
              </option>
            ))}
          </select>

          <select
            value={fMaquina}
            onChange={(e) => setFMaquina(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #334155",
              
              fontSize: 12,
            }}
          >
            <option value="todas">Todas máquinas</option>
            {maquinasUnicas.map((m) => (
              <option key={m.id} value={m.id}>
                {m.nome}
              </option>
            ))}
          </select>

          <select
            value={fStatus}
            onChange={(e) => setFStatus(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #334155",
              
              fontSize: 12,
            }}
          >
            <option value="todas">Todos status</option>
            {statusUnicos.map((st, idx) => (
              <option key={idx} value={st.id || "null"}>
                {st.nome}
              </option>
            ))}
          </select>

          <select
            value={fParada}
            onChange={(e) => setFParada(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 999,
              border: "1px solid #334155",
              
              fontSize: 12,
            }}
          >
            <option value="todas">Máquina parada? (todas)</option>
            <option value="sim">Somente parada</option>
            <option value="nao">Somente operando</option>
          </select>
        </div>

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando OS...</p>
          ) : sortedData.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhuma OS encontrada.</p>
          ) : (
            <table className="pc-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th style={{ width: 64 }}>Foto</th>
                  <SortableHeader
                    label="Código"
                    sortKey="codigoSequencial"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <SortableHeader
                    label="Abertura"
                    sortKey="criadoEm"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <SortableHeader
                    label="Empresa"
                    sortKey="empresaNome"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <SortableHeader
                    label="Máquina"
                    sortKey="maquinaNome"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <th>Parou?</th>
                  <SortableHeader
                    label="Status"
                    sortKey="statusNome"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <SortableHeader
                    label="Criticidade"
                    sortKey="criticidade"
                    requestSort={requestSort}
                    getSortDirection={getSortDirection}
                  />
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((os) => {
                  const isExpanded = expandedId === os.id;
                      const fotoUrl = os.fotoAberturaUrl || (Array.isArray(os.fotosAbertura) ? os.fotosAbertura[0] : null);
                  const corStatus = os.statusCor || "#64748b";
                  const parouTexto = String(os.maquinaParou || "").toLowerCase();
                  const parou = ["sim", "s", "true", "1", "yes"].includes(parouTexto);

                  return (
                    <React.Fragment key={os.id}>
                      <tr
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleExpand(os.id)}
                      >
                        <td style={{ textAlign: "center", padding: "8px 4px" }}>
                          <button
                            className="pc-btn-icon"
                            title={isExpanded ? "Recolher detalhes" : "Expandir detalhes"}
                            aria-expanded={isExpanded}
                            aria-label={isExpanded ? "Recolher detalhes" : "Expandir detalhes"}
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpand(os.id);
                            }}
                            style={{
                              background: isExpanded
                                ? "rgba(249, 115, 22, 0.2)"
                                : "transparent",
                              border: "1px solid rgba(148, 163, 184, 0.3)",
                            }}
                          >
                            <ChevronIcon expanded={isExpanded} />
                          </button>
                        </td>
                        <td style={{ textAlign: "center", padding: "8px 4px" }}>
                          {fotoUrl ? (
                            <button
                              type="button"
                              className="os-thumb-btn"
                              title="Abrir foto da OS"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(fotoUrl, "_blank");
                              }}
                            >
                              <img
                                className="os-thumb-img"
                                src={fotoUrl}
                                alt="Foto da OS"
                                loading="lazy"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            </button>
                          ) : (
                            <span style={{ opacity: 0.5 }}>—</span>
                          )}
                        </td>
                        <td style={{ fontWeight: 600 }}>{renderCodigoOs(os)}</td>
                        <td style={{ whiteSpace: "nowrap", fontSize: "0.85em" }}>
                          {formatDataHora(os.criadoEm)}
                          {podeEditarHorarios && (
                            <button
                              type="button"
                              onClick={() => handleEditarHorarioOS(os)}
                              title="Editar horário"
                              style={{
                                marginLeft: 10,
                                padding: "6px 10px",
                                borderRadius: 10,
                                border: "1px solid rgba(148, 163, 184, 0.35)",
                                background: "rgba(148, 163, 184, 0.12)",
                                color: "inherit",
                                cursor: "pointer",
                                fontWeight: 700,
                              }}
                            >
                              Editar
                            </button>
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{os.empresaNome || "-"}</div>
                          {os.setorNome && (
                            <div style={{ fontSize: "0.8em", opacity: 0.7 }}>{os.setorNome}</div>
                          )}
                        </td>
                        <td>
                          <div>{os.maquinaNome || "-"}</div>
</td>
                        <td>
                          {parou ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "2px 8px",
                                borderRadius: 999,
                                fontSize: "0.75em",
                                fontWeight: 600,
                                background: "rgba(239, 68, 68, 0.15)",
                                color: "#ef4444",
                                border: "1px solid rgba(239, 68, 68, 0.3)",
                              }}
                            >
                              ⚠ Sim
                            </span>
                          ) : (
                            <span style={{ opacity: 0.6, fontSize: "0.85em" }}>Não</span>
                          )}
                        </td>
                        <td>
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "4px 10px",
                              borderRadius: 999,
                              fontSize: "0.75em",
                              fontWeight: 600,
                              background: `${corStatus}20`,
                              color: corStatus,
                              border: `1px solid ${corStatus}40`,
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: corStatus,
                              }}
                            />
                            {os.statusNome || "-"}
                          </span>
                        </td>
                        <td>
                          {os.criticidade ? (
                            <span
                              style={{
                                padding: "2px 8px",
                                borderRadius: 999,
                                fontSize: "0.75em",
                                fontWeight: 500,
                                background:
                                  os.criticidade.toLowerCase().includes("alta") ||
                                  os.criticidade.toLowerCase().includes("urgente")
                                    ? "rgba(239, 68, 68, 0.15)"
                                    : os.criticidade.toLowerCase().includes("media") ||
                                      os.criticidade.toLowerCase().includes("média")
                                    ? "rgba(245, 158, 11, 0.15)"
                                    : "rgba(34, 197, 94, 0.15)",
                                color:
                                  os.criticidade.toLowerCase().includes("alta") ||
                                  os.criticidade.toLowerCase().includes("urgente")
                                    ? "#ef4444"
                                    : os.criticidade.toLowerCase().includes("media") ||
                                      os.criticidade.toLowerCase().includes("média")
                                    ? "#f59e0b"
                                    : "#22c55e",
                              }}
                            >
                              {os.criticidade}
                            </span>
                          ) : (
                            <span style={{ opacity: 0.5 }}>-</span>
                          )}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 4 }}>
                            <button
                              className="pc-btn-icon"
                              title="Trocar Status"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAbrirTrocaStatus(os);
                              }}
                              style={{ padding: 6, color: "#10b981" }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 12a9 9 0 1 1-9-9"/>
                                <path d="M21 3v9h-9"/>
                              </svg>
                            </button>
                            <button
                              className="pc-btn-icon"
                              title="Ver Atendimento"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVerDetalhes(os);
                              }}
                              style={{ padding: 6 }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                              </svg>
                            </button>
                            <button
                              className="pc-btn-icon"
                              title="Editar"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditar(os);
                              }}
                              style={{ padding: 6 }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            <button
                              className="pc-btn-icon"
                              title="Deletar"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExcluir(os.id);
                              }}
                              disabled={deletando === os.id}
                              style={{ padding: 6, color: deletando === os.id ? "#94a3b8" : "#ef4444" }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>

                      {isExpanded && (
                        <tr className="row-details-expanded">
                          <td colSpan={10} style={{ padding: 0 }}>
                            <div
                              style={{
                                background: "rgba(15, 23, 42, 0.4)",
                                borderTop: "1px solid rgba(148, 163, 184, 0.1)",
                                borderBottom: "1px solid rgba(148, 163, 184, 0.1)",
                                padding: "16px 20px",
                              }}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                                  gap: 16,
                                }}
                              >
                                <div>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                                    DEFEITO APRESENTADO
                                  </div>
                                  <div style={{ fontSize: "0.9em" }}>
                                    {os.defeitoApresentado || os.titulo || os.descricao || "-"}
                                  </div>
                                </div>

                                <div>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                                    TIPO DE SERVIÇO
                                  </div>
                                  <div style={{ fontSize: "0.9em" }}>
                                    {os.tipoServico || "-"}
                                  </div>
                                </div>

                                <div>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                                    ABERTA POR
                                  </div>
                                  <div style={{ fontSize: "0.9em" }}>
                                    {os.criadoPorNome || os.abertaPorNome || "-"}
                                  </div>
                                </div>

                                <div>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                                    MÁQUINA CHAVE
                                  </div>
                                  <div style={{ fontSize: "0.9em" }}>
                                    {os.maquinaChave ? "Sim" : "Não"}
                                  </div>
                                </div>
<div>
                                  <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                                    SETOR
                                  </div>
                                  <div style={{ fontSize: "0.9em" }}>
                                    {os.setorNome || "-"}
                                  </div>
                                </div>

                                {os.observacoes && (
                                  <div style={{ gridColumn: "1 / -1" }}>
                                    <div style={{ fontSize: "0.75em", opacity: 0.6, marginBottom: 4 }}>
                                      OBSERVAÇÕES
                                    </div>
                                    <div style={{ fontSize: "0.9em", whiteSpace: "pre-wrap" }}>
                                      {os.observacoes}
                                    </div>
                                  </div>
                                )}
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  marginTop: 16,
                                  paddingTop: 12,
                                  borderTop: "1px solid rgba(148, 163, 184, 0.15)",
                                }}
                              >
                                <button
                                  onClick={() => handleVerDetalhes(os)}
                                  style={{
                                    padding: "6px 12px",
                                    borderRadius: 6,
                                    border: "1px solid #334155",
                                    
                                    color: "var(--cor-texto)",
                                    cursor: "pointer",
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                    <circle cx="12" cy="12" r="3"/>
                                  </svg>
                                  Ver Atendimento
                                </button>
                                <button
                                  onClick={() => handleEditar(os)}
                                  style={{
                                    padding: "6px 12px",
                                    borderRadius: 6,
                                    border: "none",
                                    background: "#f97316",
                                    color: "#fff",
                                    cursor: "pointer",
                                    fontSize: 12,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                  </svg>
                                  Editar OS
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {modalTrocaStatus && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
          onClick={handleFecharTrocaStatus}
        >
          <div
            style={{
              background: "var(--cor-card-bg, #1e293b)",
              borderRadius: 12,
              padding: 24,
              maxWidth: 400,
              width: "90%",
              boxShadow: "0 20px 40px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, marginBottom: 8, fontSize: 18 }}>
              Trocar Status
            </h3>
            <p style={{ margin: 0, marginBottom: 16, opacity: 0.7, fontSize: 14 }}>
              {renderCodigoOs(modalTrocaStatus)} - {modalTrocaStatus.titulo || modalTrocaStatus.defeitoNome || "-"}
            </p>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 14, display: "block", marginBottom: 6 }}>
                Novo Status
              </label>
              <select
                value={novoStatusId}
                onChange={(e) => setNovoStatusId(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px solid var(--cor-borda-input, #334155)",
                  background: "var(--cor-input-bg, transparent)",
                  color: "var(--cor-input-texto, inherit)",
                  fontSize: 14,
                }}
              >
                <option value="">Selecione o novo status</option>
                {statusList.filter((st) => normalizarStatusId(st.id) !== normalizarStatusId(modalTrocaStatus?.statusId)).map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.nome}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="pc-btn-secundario"
                onClick={handleFecharTrocaStatus}
                disabled={salvandoStatus}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="pc-btn-primario"
                onClick={handleSalvarNovoStatus}
                disabled={salvandoStatus || !novoStatusId}
              >
                {salvandoStatus ? "Salvando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </LayoutDesktop>
  );
}

export default TabelaOSPage;
